import { z } from "zod";
import {
  EntityCreateOpSchema,
  RelationCreateOpSchema,
} from "@/lib/core/schemas/op-shapes";
import { EntityPatchOpSchema } from "@/lib/kernel/op-pipeline/patch-schema";
import type { CharacterSheetDnd5e } from "@/lib/rules/dnd5e/character/character-sheet";
import { Dnd5eAbilityScoreTagSchema } from "@/lib/rules/dnd5e/vocabulary/ability-score";

export const CharacterCreationChoiceValueSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("single"), value: z.string().min(1) }),
  z.object({
    kind: z.literal("multi"),
    values: z.array(z.string().min(1)),
  }),
  z.object({
    kind: z.literal("ability-allocation"),
    values: z.record(Dnd5eAbilityScoreTagSchema, z.number()),
  }),
  z.object({
    kind: z.literal("dependent"),
    source: z.string().min(1),
    values: z.record(z.union([z.string(), z.array(z.string())])),
  }),
  z.object({
    kind: z.literal("equipment"),
    classBundle: z.string().optional(),
    backgroundBundle: z.string().optional(),
  }),
]);
export type CharacterCreationChoiceValue = z.infer<
  typeof CharacterCreationChoiceValueSchema
>;

export const CharacterCreationDraftDnd5eSchema = z.object({
  creationId: z.string().uuid(),
  name: z.string().trim().min(1).optional(),
  speciesId: z.string().optional(),
  className: z.string().optional(),
  backgroundId: z.string().nullable().optional(),
  abilityScores: z.record(Dnd5eAbilityScoreTagSchema, z.number()).optional(),
  choices: z.record(CharacterCreationChoiceValueSchema).default({}),
});
export type CharacterCreationDraftDnd5e = z.infer<
  typeof CharacterCreationDraftDnd5eSchema
>;

export const CharacterCreationChoiceOptionDnd5eSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  description: z.string().optional(),
  disabledReason: z
    .object({
      code: z.string().min(1),
      message: z.string().min(1),
    })
    .optional(),
});
export type CharacterCreationChoiceOptionDnd5e = z.infer<
  typeof CharacterCreationChoiceOptionDnd5eSchema
>;

export const CharacterCreationChoiceNodeDnd5eSchema = z.object({
  id: z.string().min(1),
  kind: z.enum([
    "single",
    "multi",
    "ability-allocation",
    "dependent",
    "equipment",
  ]),
  category: z.enum([
    "identity",
    "species",
    "class",
    "background",
    "feat",
    "proficiency",
    "expertise",
    "language",
    "spell",
    "class-option",
    "fighting-style",
    "weapon-mastery",
    "equipment",
  ]),
  label: z.string().min(1),
  description: z.string().optional(),
  required: z.boolean(),
  count: z.number().int().positive().optional(),
  options: z.array(CharacterCreationChoiceOptionDnd5eSchema),
  dependsOn: z.array(z.string()),
  source: z.object({
    profileId: z.string().optional(),
    field: z.string().optional(),
    entitlement: z.string().optional(),
  }),
  selection: CharacterCreationChoiceValueSchema.optional(),
});
export type CharacterCreationChoiceNodeDnd5e = z.infer<
  typeof CharacterCreationChoiceNodeDnd5eSchema
>;

export const CharacterCreationInvalidationDnd5eSchema = z.object({
  nodeId: z.string().min(1),
  code: z.enum([
    "upstream-changed",
    "invalid-option",
    "duplicate-choice",
    "wrong-cardinality",
  ]),
  removedValue: CharacterCreationChoiceValueSchema.optional(),
  detail: z.string().optional(),
});
export type CharacterCreationInvalidationDnd5e = z.infer<
  typeof CharacterCreationInvalidationDnd5eSchema
>;

export const CharacterCreationValidationErrorDnd5eSchema = z.object({
  nodeId: z.string().optional(),
  code: z.string().min(1),
  detail: z.string().optional(),
});
export type CharacterCreationValidationErrorDnd5e = z.infer<
  typeof CharacterCreationValidationErrorDnd5eSchema
>;

export const CharacterCreationSelectedChoiceReceiptDnd5eSchema = z.object({
  nodeId: z.string().min(1),
  sourceId: z.string().min(1),
  value: CharacterCreationChoiceValueSchema,
});
export type CharacterCreationSelectedChoiceReceiptDnd5e = z.infer<
  typeof CharacterCreationSelectedChoiceReceiptDnd5eSchema
>;

export const CharacterCreationOpPlanDnd5eSchema = z.object({
  actorPlaceholder: z.literal("pc"),
  entityCreateOps: z.array(EntityCreateOpSchema),
  entityPatchOps: z.array(EntityPatchOpSchema),
  relationOps: z.array(RelationCreateOpSchema),
  entityDestroyOps: z.tuple([]),
  selectedChoiceReceipts: z.array(
    CharacterCreationSelectedChoiceReceiptDnd5eSchema
  ),
});
export type CharacterCreationOpPlanDnd5e = z.infer<
  typeof CharacterCreationOpPlanDnd5eSchema
>;

export const CharacterCreationInventoryItemDnd5eSchema = z.object({
  temporaryEntityId: z.string().min(1),
  definitionId: z.string().min(1),
  label: z.string().min(1),
  quantity: z.number().int().positive().default(1),
  sourceNodeId: z.string().min(1),
});
export type CharacterCreationInventoryItemDnd5e = z.infer<
  typeof CharacterCreationInventoryItemDnd5eSchema
>;

export const CharacterCreationChoiceSummaryDnd5eSchema = z.object({
  nodeId: z.string().min(1),
  category: CharacterCreationChoiceNodeDnd5eSchema.shape.category,
  label: z.string().min(1),
  selectedLabels: z.array(z.string().min(1)),
});
export type CharacterCreationChoiceSummaryDnd5e = z.infer<
  typeof CharacterCreationChoiceSummaryDnd5eSchema
>;

export const CharacterCreationPreviewProvenanceDnd5eSchema = z.object({
  sectionKey: z.string().min(1),
  valueKey: z.string().min(1).optional(),
  sourceNodeIds: z.array(z.string().min(1)).min(1),
});
export type CharacterCreationPreviewProvenanceDnd5e = z.infer<
  typeof CharacterCreationPreviewProvenanceDnd5eSchema
>;

export const CharacterCreationEvaluationDnd5eSchema = z.object({
  normalizedDraft: CharacterCreationDraftDnd5eSchema,
  status: z.enum(["incomplete", "complete", "invalid"]),
  nodes: z.array(CharacterCreationChoiceNodeDnd5eSchema),
  pendingNodeIds: z.array(z.string()),
  invalidations: z.array(CharacterCreationInvalidationDnd5eSchema),
  errors: z.array(CharacterCreationValidationErrorDnd5eSchema),
  preview: z
    .object({
      sheet: z.custom<CharacterSheetDnd5e>(),
      inventory: z.array(CharacterCreationInventoryItemDnd5eSchema),
      choiceSummary: z.array(CharacterCreationChoiceSummaryDnd5eSchema),
      provenance: z.array(CharacterCreationPreviewProvenanceDnd5eSchema),
      planDigest: z.string(),
      evaluatedStateVersion: z.number().int().nonnegative(),
      plan: CharacterCreationOpPlanDnd5eSchema.optional(),
    })
    .optional(),
});
export type CharacterCreationEvaluationDnd5e = z.infer<
  typeof CharacterCreationEvaluationDnd5eSchema
>;

export const CommitCharacterCreationDnd5eInputSchema = z.object({
  draft: CharacterCreationDraftDnd5eSchema,
  expectedPlanDigest: z.string().min(1),
});
export type CommitCharacterCreationDnd5eInput = z.infer<
  typeof CommitCharacterCreationDnd5eInputSchema
>;

export const CommitCharacterCreationDnd5eResultSchema = z.object({
  actorId: z.string().min(1),
  creationId: z.string().uuid(),
  planDigest: z.string().min(1),
  replayed: z.boolean(),
  sheet: z.custom<CharacterSheetDnd5e>(),
});
export type CommitCharacterCreationDnd5eResult = z.infer<
  typeof CommitCharacterCreationDnd5eResultSchema
>;
